// Top-10 criteria by dispute volume, stacked by overturned / upheld /
// pending (merged "Most-Disputed Criteria" + "Dispute Overturn Rate").
import React, { useMemo } from 'react'
import { isEmpty, isNil } from 'lodash'

import { apiService } from '@/api'

import { CHART } from '@/components/charts/chartPalette'
import { escapeHtml } from '../helpers/format'
import { ChartSortControl } from '../components/ChartSortControl'
import { useChartSort } from '../hooks/useChartSort'
import { ChartWidgetCard, axisTooltip, useEntityClickEvents, useWidgetData } from './chartWidget'

const SORT_OPTIONS = [
  { key: 'volume', label: 'Dispute volume', get: (row) => row.total },
  { key: 'overturn', label: 'Overturn rate', get: (row) => row.overturnRatePct },
]

export const DisputedCriteriaLeaderboardWidget = ({ scope }) => {
  // /disputes/all currently honors only start_date/end_date/
  // scorecard_type; the other dims are forwarded for when BE adds them.
  const {
    data: disputes,
    loading,
    error,
  } = useWidgetData(scope, (params) => `${apiService.scorecard}/disputes/all?${params.toString()}`)

  const sort = useChartSort(SORT_OPTIONS, { defaultKey: 'volume', defaultDir: 'desc' })

  // Aggregate by criteria_name; a sample dispute row supplies the
  // criterion id + scorecard_config_sid for the drilldown payload.
  const rows = useMemo(() => {
    const byCriterion = {}
    const safeDisputes = disputes || []
    safeDisputes.forEach((dispute) => {
      const name = dispute.criteria_name || 'Unknown'
      if (!byCriterion[name]) {
        byCriterion[name] = {
          total: 0,
          accepted: 0,
          declined: 0,
          pending: 0,
          sample: dispute,
        }
      }
      const row = byCriterion[name]
      row.total += 1
      if (dispute.status === 'accepted') {
        row.accepted += 1
      } else if (dispute.status === 'declined') {
        row.declined += 1
      } else {
        row.pending += 1
      }
    })
    return Object.entries(byCriterion)
      .map(([name, counts]) => {
        const resolved = counts.accepted + counts.declined
        return {
          name,
          total: counts.total,
          accepted: counts.accepted,
          declined: counts.declined,
          pending: counts.pending,
          // Overturn rate counts resolved disputes only; a dash renders
          // when nothing is resolved yet.
          overturnRatePct: resolved > 0 ? Math.round((counts.accepted / resolved) * 100) : null,
          criterion: {
            criteria_name: name,
            name,
            id: counts.sample?.criteria_id,
            criteria_id: counts.sample?.criteria_id,
            scorecard_config_sid: counts.sample?.scorecard_config_sid,
            scorecard_config_name: counts.sample?.scorecard_config_name,
          },
        }
      })
      .sort(sort.comparator)
      .slice(0, 10)
  }, [disputes, sort.comparator])

  const option = useMemo(() => {
    if (isEmpty(rows)) {
      return {}
    }
    return {
      tooltip: axisTooltip((params) => {
        const row = rows[params[0].dataIndex]
        const overturn = isNil(row.overturnRatePct) ? '—' : `${row.overturnRatePct}%`
        return [
          escapeHtml(row.name),
          `Total disputes: ${row.total}`,
          `Overturned: ${row.accepted}`,
          `Upheld: ${row.declined}`,
          `Pending: ${row.pending}`,
          `Overturn rate (of resolved): ${overturn}`,
        ].join('<br/>')
      }),
      legend: { top: 0, textStyle: { fontSize: 11 } },
      grid: { left: 140, right: 24, top: 30, bottom: 16 },
      xAxis: { type: 'value', name: 'Disputes' },
      yAxis: {
        type: 'category',
        data: rows.map((row) => row.name),
        inverse: true,
        axisLabel: { fontSize: 10 },
      },
      series: [
        {
          name: 'Overturned',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: CHART.CRITICAL },
          cursor: 'pointer',
          data: rows.map((row) => ({ value: row.accepted, criterion: row.criterion })),
        },
        {
          name: 'Upheld',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: CHART.SUCCESS },
          cursor: 'pointer',
          data: rows.map((row) => ({ value: row.declined, criterion: row.criterion })),
        },
        {
          name: 'Pending',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: CHART.MUTED },
          cursor: 'pointer',
          data: rows.map((row) => ({ value: row.pending, criterion: row.criterion })),
        },
      ],
    }
  }, [rows])

  const events = useEntityClickEvents('criterion', 'criterion')

  return (
    <ChartWidgetCard
      title="Dispute overturn rate"
      info="For the most-disputed criteria, how many disputes were overturned (score changed) vs upheld vs still pending."
      error={error}
      empty={isEmpty(rows)}
      emptyText="No disputes in this window."
      loading={loading}
      option={option}
      events={events}
      height="320px"
    >
      <ChartSortControl sort={sort} />
    </ChartWidgetCard>
  )
}
